import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BundleProductSchema } from 'src/app/models/bundle-product.schema';
import { FormGroupToObject } from 'src/app/pipes/form-group-to-object.pipe';
import { AuthStatusService } from 'src/app/services/auth-status.service';
import { BundleProductsService } from 'src/app/services/bundle-products.service';
import { CommanUsesService } from 'src/app/services/comman-uses.service';
import { SpinnerService } from 'src/app/services/spinner.service';

@Component({
  selector: 'app-bundle-product',
  templateUrl: './bundle-product.component.html',
  styleUrls: ['./bundle-product.component.scss']
})
export class BundleProductComponent implements OnInit {

  groups: any;
  bundleProducts: BundleProductSchema[] = [];
  defaultPage = 1;
  defaultLimit = 50;
  defaultOrder: any = { 'BundleProduct.id': 'DESC' };

  limit: number = this.defaultLimit;
  page: number = this.defaultPage;
  count: number = 0;
  maxPage: number = 0;
  filtered: any = {};
  order: any = { ...this.defaultOrder };

  filterForm!: FormGroup;
  provinceFilter: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private commanUsesService: CommanUsesService,
    private spinnerService: SpinnerService,
    private authStatusService: AuthStatusService,
    private bundleProductsService: BundleProductsService,
  ) { 
    this.groups = this.authStatusService.getRoles().map((elem: string) => elem.toLowerCase());
    this.manageFilterForm();
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.filter();
    } catch (error) {
      await this.commanUsesService.showSnackBar(
        error?.error?.message || 'Errore durante il caricamento'
      );
    } finally {
      this.spinnerService.hide();
    }
  }

  manageFilterForm(): void {
    this.filterForm = new FormGroup({
      'BundleProduct.id': new FormControl(''),
      'Product.name': new FormControl(''),
      'Partner.businessName': new FormControl(''),
      'province': new FormControl(''),
    });
  }

  async resetFilter() {
    this.manageFilterForm();
    this.filtered = {};
    this.provinceFilter = '';
    this.page = 1;

    await this.getBundleProducts(this.limit, this.page, this.order);
  }

  async filter() {

    let filterForm = (new FormGroupToObject()).transform(this.filterForm);
    this.filtered = {};
    this.provinceFilter = '';

    if (filterForm['BundleProduct.id'])
      this.filtered['BundleProduct.id'] = filterForm['BundleProduct.id'];
    if (filterForm['Partner.businessName'])
      this.filtered['Partner.businessName'] = { operator: 'like', values: [`%${filterForm['Partner.businessName']}%`] };
    if (filterForm['Product.name'])
      this.filtered['Product.name'] = { operator: 'like', values: [`%${filterForm['Product.name']}%`]};
    if (filterForm['province']) 
      this.provinceFilter = filterForm['province'].trim().toUpperCase();

    await this.getBundleProducts();
  }

  async getBundleProducts(limit?: number, page?: number, order?: {}){
    
    let filterForm = (new FormGroupToObject()).transform(this.filterForm);
    this.page = page || parseInt(this.route.snapshot.queryParamMap.get('page')) || this.defaultPage;
    this.limit = limit || parseInt(this.route.snapshot.queryParamMap.get('limit')) || this.defaultLimit;
    this.order = order || JSON.parse(this.route.snapshot.queryParamMap.get('order')) || this.defaultOrder;
    let response = await this.bundleProductsService.getAllBundleProducts(this.page, this.limit, this.order, this.filtered, this.provinceFilter);
    this.bundleProducts = response.bundleProducts || [];
    this.count = response.count;
    this.maxPage = Math.ceil(this.count / this.limit);
  }

  getLink(bundleProduct: BundleProductSchema): any[] {
    return ['/bundle/product/edit', bundleProduct.id];
  }

  async delete(bundleProduct: any): Promise<void> {
    const id = typeof bundleProduct === 'object'
      ? bundleProduct.id
      : bundleProduct;

    if (!id) {
      await this.commanUsesService.showSnackBar('ID prodotto in bundle non valido');
      return;
    }

    const confirmed = confirm('Sei sicuro di voler eliminare questo prodotto in bundle?');

    if (!confirmed) {
      return;
    }

    this.spinnerService.show();

    try {
      await this.bundleProductsService.delete(id);

      await this.commanUsesService.showSnackBar('Prodotto in bundle eliminato');

      await this.getBundleProducts();

    } catch (error: any) {
      await this.commanUsesService.showSnackBar(
        error?.error?.message || 'Errore durante eliminazione'
      );
    } finally {
      this.spinnerService.hide();
    }
  }

  public orderIcon(attribute: string | string[]): string {
    attribute = Array.isArray(attribute) ? attribute : [attribute];
    for (let attr of attribute) {
      switch (this.order[attr]) {
        case 'ASC':
          return 'fa-sort-up';
        case 'DESC':
          return 'fa-sort-down';
        default:
          return 'fa-sort';
      }
    }
  }

  public async navigateOrder(attribute: string | string[]) {
    delete this.order['BundleProduct.updatedAt'];
    attribute = Array.isArray(attribute) ? attribute : [attribute];
    for (let attr of attribute) {
      switch (this.order[attr]) {
        case 'ASC':
          this.order[attr] = 'DESC';
          break;
        case 'DESC':
          this.order[attr] = undefined;
          break;
        default:
          this.order = {}
          this.order[attr] = 'ASC';
          break;
      }
    }

    this.navigatePage(this.limit, this.page, { order: this.order });
  }

  navigatePage(limit: number, page: number, args: {} = {}) {
    args = this.escapeQueryStringParams(args);
    const previousArgs = Object.keys(args).length ? {} : this.route.snapshot.queryParams;
    const newParams = { ...previousArgs };
    newParams[`limit`] = limit;
    newParams[`page`] = page;
    this.router.navigate(this.route.snapshot.url.map(u => u.path), { queryParams: { ...newParams, ...args } }).then(async (data) => {
      await this.getBundleProducts();
    });
  }

  navigate(event) {
    this.getBundleProducts(event.limit, event.page, this.order);
  }

  private escapeQueryStringParams(args: {} = {}): {} {
    Object.keys(args).forEach(key => {
      switch (Object.prototype.toString.call(args[key])) {
        case '[object Number]':
        case '[object String]':
          break;
        case '[object Array]':
        case '[object Object]':
          args[key] = JSON.stringify(args[key]);
          break;
        default:
          delete args[key];
          break;
      }
    });
    return args;
  }

}
